import React, { useContext } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, Button, IconButton, Typography, useMediaQuery } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { DatePicker } from '@mui/x-date-pickers';

// Components
import { userRoles } from 'utils/currentUserRoles';
import { MAutocomplete, MDropdown } from 'common';

// Utils
import { AuthContext } from 'authentication/AuthContext';
import { useGetAffiliateOptions } from 'hooks';
import { parseISO } from 'date-fns';
import { SEARCH_STATUS_OPTIONS } from '../../utils';

const SearchPtoFilter = props => {
  const {
    currentAffiliate,
    setCurrentAffiliate,
    locationOptions,
    originatorOptions,
    productOptions,
    ptoNumberOptions,
    recipientOptions,
    referenceIdOptions,
    resetSearchFilters,
    searchFilters,
    toggleDrawer,
    updateSearchFilter,
  } = props;
  const {
    fromDate,
    location,
    originator,
    product,
    ptoNumber,
    recipient,
    referenceId,
    status,
    toDate,
  } = searchFilters;

  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const { affiliates: userAffiliates } = useContext(AuthContext);

  const userAffiliatesOptions = [
    { label: 'All Affiliates', value: 'All' },
    ...useGetAffiliateOptions([userRoles.INVENTORY_OWNER]),
  ];

  const handleAffiliateChange = ({ value }) => {
    const selectedAffiliateOption =
      userAffiliates.find(
        ({ customerIdName, dataCompanyId }) => `${customerIdName}-${dataCompanyId}` === value
      ) || null;

    setCurrentAffiliate(selectedAffiliateOption);
    resetSearchFilters();
  };

  const selectedAffiliate = currentAffiliate
    ? userAffiliatesOptions.find(
        ({ customerIdName, dataCompanyId }) =>
          customerIdName === currentAffiliate.customerIdName &&
          dataCompanyId === currentAffiliate.dataCompanyId
      )
    : userAffiliatesOptions[0];

  return (
    <Box
      sx={{
        backgroundColor: 'surface.white',
        borderRight: '1px solid',
        borderRightColor: 'graphic.light',
        borderTop: { lg: '1px solid' },
        borderTopColor: 'graphic.light',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: { lg: 'fixed' },
        top: { lg: 47 },
        width: { lg: 300 },
        zIndex: { lg: 10 },
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid',
          borderBottomColor: 'graphic.light',
          display: 'flex',
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          height: { xs: 64, lg: 48 },
          justifyContent: 'space-between',
          pl: 2,
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          pr: { xs: 1, lg: 0 },
          py: 0,
        }}
      >
        <Typography
          sx={{
            color: 'primary.dark',
            pt: '2px',
          }}
          variant="subtitle1"
        >
          Filter Search:
        </Typography>
        {lgUp ? (
          <Button onClick={resetSearchFilters} sx={{ minWidth: 40, mr: 2, p: 0 }} variant="text">
            Clear
          </Button>
        ) : (
          <IconButton
            onClick={() => toggleDrawer()}
            sx={{
              ':hover': {
                backgroundColor: 'transparent',
              },
              color: 'primary.light',
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Box>
      <Box
        sx={{
          '& > div': {
            marginTop: 2,
          },
          display: 'flex',
          flexDirection: 'column',
          padding: (0, 2),
        }}
      >
        {userAffiliatesOptions.length > 2 && (
          <MDropdown
            anchorOrigin={{
              horizontal: 'center',
              vertical: 'top',
            }}
            id="originator"
            items={userAffiliatesOptions}
            label="Affiliate"
            name="affiliate"
            onChange={handleAffiliateChange}
            value={selectedAffiliate.value}
          />
        )}
        <DatePicker
          label="Start Date"
          maxDate={parseISO(toDate)}
          onChange={newValue => updateSearchFilter('fromDate', newValue.toJSON())}
          slotProps={{
            textField: {
              sx: { '& > div > input': { py: '12.5px' } },
            },
          }}
          value={parseISO(fromDate)}
        />
        <DatePicker
          label="End Date"
          minDate={parseISO(fromDate)}
          onChange={newValue => updateSearchFilter('toDate', newValue.toJSON())}
          slotProps={{
            textField: {
              sx: { '& > div > input': { py: '12.5px' } },
            },
          }}
          value={parseISO(toDate)}
        />
        <MDropdown
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          id="status-select"
          items={SEARCH_STATUS_OPTIONS}
          label="Status"
          name="status"
          onChange={({ name, value }) => updateSearchFilter(name, value)}
          value={status}
        />
        <MAutocomplete
          id="ptoNumber-select"
          label="PTO Number"
          loading={false}
          name="ptoNumber"
          onChange={(_, val) => updateSearchFilter('ptoNumber', val?.value || null)}
          options={ptoNumberOptions}
          value={ptoNumber}
          width="100%"
        />
        <MAutocomplete
          id="referenceId-select"
          label="Reference IDs"
          loading={false}
          name="referenceId"
          onChange={(_, val) => updateSearchFilter('referenceId', val?.value || null)}
          options={referenceIdOptions}
          value={referenceId}
          width="100%"
        />
        <MAutocomplete
          classNames="css-heap-redact"
          id="originator-select"
          label="Originators"
          loading={false}
          name="originators"
          onChange={(_, val) => updateSearchFilter('originator', val?.value || null)}
          options={originatorOptions}
          value={originator}
          width="100%"
        />
        <MAutocomplete
          classNames="css-heap-redact"
          id="recipient-select"
          label="Recipients"
          loading={false}
          name="recipients"
          onChange={(_, val) => updateSearchFilter('recipient', val?.value || null)}
          options={recipientOptions}
          value={recipient}
          width="100%"
        />
        <MAutocomplete
          id="location-select"
          label="Locations"
          loading={false}
          name="location"
          onChange={(_, val) => updateSearchFilter('location', val?.value || null)}
          options={locationOptions}
          value={location}
          width="100%"
        />
        <MAutocomplete
          classNames="css-heap-redact"
          id="product-select"
          label="Products"
          loading={false}
          name="product"
          onChange={(_, val) => updateSearchFilter('product', val?.value || null)}
          options={productOptions}
          value={product}
          width="100%"
        />
        {!lgUp && (
          <Button
            onClick={resetSearchFilters}
            sx={{ margin: '0 auto', maxWidth: 112, mt: 2 }}
            variant="text"
          >
            Clear
          </Button>
        )}
      </Box>
    </Box>
  );
};

SearchPtoFilter.propTypes = {
  currentAffiliate: shape({}),
  location: shape({
    search: string,
    state: shape({ showClosed: bool, showOpen: bool }),
  }),
  locationOptions: arrayOf(shape({ label: string, value: string })).isRequired,
  originatorOptions: arrayOf(shape({ label: string, value: string })).isRequired,
  productOptions: arrayOf(shape({ label: string, value: string })).isRequired,
  ptoNumberOptions: arrayOf(shape({ label: string, value: string })).isRequired,
  recipientOptions: arrayOf(shape({ label: string, value: string })).isRequired,
  referenceIdOptions: arrayOf(shape({ label: string, value: string })).isRequired,
  resetSearchFilters: func.isRequired,
  searchFilters: shape({
    fromDate: string,
    location: oneOfType([string, number]),
    originator: oneOfType([string, number]),
    product: oneOfType([string, number]),
    ptoNumber: oneOfType([string, number]),
    recipient: oneOfType([string, number]),
    referenceId: oneOfType([string, number]),
    status: string,
    toDate: string,
  }).isRequired,
  setCurrentAffiliate: func.isRequired,
  toggleDrawer: func,
  updateSearchFilter: func.isRequired,
};

SearchPtoFilter.defaultProps = {
  currentAffiliate: '',
  location: {
    search: '',
    state: {},
  },
  toggleDrawer: () => {},
};
// taylor - test navigate and other hooks still work
export default SearchPtoFilter;
